/**
 * Formatting primitives. Money and weights are never rendered raw — they go through
 * here so every column lines up and nothing shows a floating-point tail.
 */

const KES = new Intl.NumberFormat('en-KE', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})
const KES0 = new Intl.NumberFormat('en-KE', { maximumFractionDigits: 0 })

export function money(value: number | string | null | undefined, opts?: { compact?: boolean; currency?: boolean }) {
  const n = toNumber(value)
  if (n === null) return '—'
  const prefix = opts?.currency === false ? '' : 'KES '

  if (opts?.compact) {
    if (Math.abs(n) >= 1_000_000) return `${prefix}${(n / 1_000_000).toFixed(2)}M`
    if (Math.abs(n) >= 10_000) return `${prefix}${KES0.format(Math.round(n / 1000))}K`
    return prefix + KES0.format(n)
  }
  return prefix + KES.format(n)
}

export function kg(value: number | string | null | undefined, decimals = 1) {
  const n = toNumber(value)
  if (n === null) return '—'
  return `${n.toFixed(decimals)} kg`
}

export function tonnes(value: number | string | null | undefined) {
  const n = toNumber(value)
  if (n === null) return '—'
  return n >= 1000 ? `${(n / 1000).toFixed(1)} T` : `${Math.round(n)} kg`
}

export function pct(value: number | string | null | undefined, decimals = 1) {
  const n = toNumber(value)
  if (n === null) return '—'
  return `${n.toFixed(decimals)}%`
}

export function signedPct(value: number | string | null | undefined, decimals = 1) {
  const n = toNumber(value)
  if (n === null) return '—'
  const sign = n > 0 ? '+' : ''
  return `${sign}${n.toFixed(decimals)}%`
}

export function num(value: number | string | null | undefined, decimals = 0) {
  const n = toNumber(value)
  if (n === null) return '—'
  return n.toLocaleString('en-KE', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
}

export function toNumber(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : null
}

// ---------------------------------------------------------------- time
export function time(iso: string | null | undefined) {
  if (!iso) return '—'
  return new Date(iso).toLocaleTimeString('en-KE', { hour: '2-digit', minute: '2-digit', hour12: false })
}

export function date(iso: string | null | undefined) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' })
}

export function dateTime(iso: string | null | undefined) {
  if (!iso) return '—'
  return `${date(iso)} ${time(iso)}`
}

export function shortDate(iso: string | null | undefined) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-KE', { day: 'numeric', month: 'short' })
}

export function ago(iso: string | null | undefined) {
  if (!iso) return '—'
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.round(diff / 60_000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins} min ago`
  const hrs = Math.round(mins / 60)
  if (hrs < 24) return `${hrs} h ago`
  return `${Math.round(hrs / 24)} d ago`
}

/** '13:00:00' from Postgres time columns -> '13:00' */
export function clock(t: string | null | undefined) {
  if (!t) return '—'
  return t.slice(0, 5)
}

// ---------------------------------------------------------------- text
export function initials(name: string | null | undefined) {
  if (!name) return '··'
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join('')
}

export function titleCase(s: string | null | undefined) {
  if (!s) return ''
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

/** Kenyan msisdn, masked for display: +254712000101 -> 0712 ••• 101 */
export function maskedPhone(msisdn: string | null | undefined) {
  if (!msisdn) return '—'
  const local = msisdn.replace(/^\+254/, '0')
  if (local.length < 8) return local
  return `${local.slice(0, 4)} ••• ${local.slice(-3)}`
}

/**
 * Semantic state for a number against a target.
 * No data is not a warning — an unmeasured metric stays neutral so colour is only
 * ever spent on something that needs a person.
 */
export function against(
  value: number | null | undefined,
  target: number,
  higherIsBetter = true,
): 'ok' | 'warn' | 'crit' | 'neutral' {
  if (value === null || value === undefined) return 'neutral'
  const ratio = target === 0 ? 1 : value / target
  if (higherIsBetter) {
    if (ratio >= 1) return 'ok'
    return ratio >= 0.9 ? 'warn' : 'crit'
  }
  if (ratio <= 1) return 'ok'
  return ratio <= 1.25 ? 'warn' : 'crit'
}
